/* eslint-disable @typescript-eslint/no-explicit-any --
   Catalog page handles dynamic product data from global + tenant sources. */
import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import type { CatalogSearchResult, GlobalProduct } from '@shared/types'
import { TableSkeleton, CardGridSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { ProductImage, getManufacturerImageUrl } from '../components/ProductImage'
import { GlowCard } from '../components/ui/GlowCard'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

type TabMode = 'my-catalog' | 'library'

const EMPTY_FORM = {
  name: '', mpn: '', manufacturer: '', category: '', cost: 0, sell_price: 0,
  description: '', preferred_supplier: '', image_url: '',
}

const TH: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em',
}

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'camera', label: 'Camera' },
  { value: 'nvr', label: 'NVR' },
  { value: 'access_control', label: 'Access Control' },
  { value: 'networking', label: 'Networking' },
  { value: 'cable', label: 'Cable' },
  { value: 'mount', label: 'Mount' },
  { value: 'switch', label: 'Switch' },
  { value: 'audio', label: 'Audio' },
  { value: 'power', label: 'Power/UPS' },
  { value: 'labor', label: 'Labor' },
  { value: 'other', label: 'Other' },
]

function getItemName(item: CatalogSearchResult) { return item.custom_name || item.name || 'Unknown' }
function getItemBrand(item: CatalogSearchResult) { return item.custom_brand || item.brand || '' }
function getItemMpn(item: CatalogSearchResult) { return item.custom_mpn || item.mpn || '' }

export default function Catalog() {
  const [tab, setTab] = useState<TabMode>('my-catalog')
  const [items, setItems] = useState<CatalogSearchResult[]>([])
  const [globalItems, setGlobalItems] = useState<GlobalProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [laborOnly, setLaborOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editItem, setEditItem] = useState<CatalogSearchResult | null>(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [imageUrlInput, setImageUrlInput] = useState('')
  const [savingImageUrl, setSavingImageUrl] = useState(false)
  const [fetchingToR2, setFetchingToR2] = useState(false)
  const [brands, setBrands] = useState<string[]>([])
  const [addingId, setAddingId] = useState<string | null>(null)
  const [addForm, setAddForm] = useState({ cost: 0, sellPrice: 0, supplier: '' })
  const [specsForm, setSpecsForm] = useState({ form_factor: '', rack_units: 0, weight_lbs: 0, power_draw_watts: 0 })
  const [specsLoading, setSpecsLoading] = useState(false)
  const [savingSpecs, setSavingSpecs] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20', includeGlobal: 'false' })
      if (search) params.set('search', search)
      if (category) params.set('category', category)
      if (brandFilter) params.set('manufacturer', brandFilter)
      if (favoritesOnly) params.set('favorites', 'true')
      if (laborOnly) params.set('category', 'labor')
      const result = await api.get<{ data: CatalogSearchResult[]; total: number; totalPages: number }>(`/catalog?${params}`)
      setItems(result.data ?? [])
      setTotalPages(result.totalPages ?? 1)
      setTotal(result.total ?? 0)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, search, category, brandFilter, favoritesOnly, laborOnly])

  const loadGlobalItems = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20', includeGlobal: 'true' })
      if (search) params.set('search', search)
      if (category) params.set('category', category)
      const result = await api.get<{ globalProducts: GlobalProduct[] }>(`/catalog?${params}`)
      setGlobalItems(result.globalProducts ?? [])
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, search, category])

  // Load top brands once
  useEffect(() => {
    api.get<string[]>('/catalog/brands').then(res => {
      setBrands(Array.isArray(res) ? res : [])
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (tab === 'my-catalog') {
      loadItems()
    } else {
      if (search) {
        loadGlobalItems()
      } else {
        setGlobalItems([])
        setLoading(false)
      }
    }
  }, [tab, loadItems, loadGlobalItems, search])

  function openAdd() {
    setEditItem(null)
    setFormData(EMPTY_FORM)
    setImageUrlInput('')
    setDrawerOpen(true)
  }

  function openEdit(item: CatalogSearchResult) {
    setEditItem(item)
    setFormData({
      name: getItemName(item),
      mpn: getItemMpn(item),
      manufacturer: getItemBrand(item),
      category: item.category ?? item.custom_category ?? '',
      cost: item.cost,
      sell_price: item.sell_price,
      description: item.custom_description || item.description || '',
      preferred_supplier: item.preferred_supplier ?? '',
      image_url: item.image_url ?? '',
    })
    setImageUrlInput(item.image_url ?? '')
    setDrawerOpen(true)

    // Load physical specs
    setSpecsForm({ form_factor: '', rack_units: 0, weight_lbs: 0, power_draw_watts: 0 })
    setSpecsLoading(true)
    api.get<Record<string, any>>(`/catalog/${item.slug}/specs`)
      .then(specs => {
        if (specs) {
          setSpecsForm({
            form_factor: specs.form_factor || '',
            rack_units: specs.rack_units || 0,
            weight_lbs: specs.weight_lbs || 0,
            power_draw_watts: specs.power_draw_watts || 0,
          })
        }
      })
      .catch(() => {})
      .finally(() => setSpecsLoading(false))
  }

  async function handleSaveSpecs() {
    if (!editItem) return
    setSavingSpecs(true)
    try {
      await api.patch(`/catalog/${editItem.slug}/specs`, {
        form_factor: specsForm.form_factor || null,
        rack_units: specsForm.rack_units || null,
        weight_lbs: specsForm.weight_lbs || null,
        power_draw_watts: specsForm.power_draw_watts || null,
      })
    } catch { /* error */ }
    finally { setSavingSpecs(false) }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      if (editItem) {
        await api.patch(`/catalog/${editItem.slug}`, {
          custom_name: formData.name || undefined,
          cost: formData.cost,
          sell_price: formData.sell_price,
          preferred_supplier: formData.preferred_supplier || undefined,
          custom_category: formData.category || undefined,
          notes: undefined,
        })
      } else {
        await api.post('/catalog', {
          name: formData.name,
          brand: formData.manufacturer || undefined,
          mpn: formData.mpn || undefined,
          category: formData.category || undefined,
          description: formData.description || undefined,
          cost: formData.cost,
          sellPrice: formData.sell_price,
          msrp: 0,
          preferredSupplier: formData.preferred_supplier || undefined,
        })
      }
      setDrawerOpen(false)
      setEditItem(null)
      setFormData(EMPTY_FORM)
      loadItems()
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  async function handleImageUpload(file: File) {
    if (!editItem) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.upload(`/catalog/${editItem.slug}/image`, fd)
      loadItems()
    } catch { /* error */ }
    finally { setUploading(false) }
  }

  async function handleSaveImageUrl() {
    if (!editItem || !imageUrlInput.trim()) return
    setSavingImageUrl(true)
    try {
      await api.patch(`/catalog/${editItem.slug}`, { image_url: imageUrlInput.trim() } as any)
      loadItems()
    } catch { /* error */ }
    finally { setSavingImageUrl(false) }
  }

  async function handleFetchToR2() {
    if (!editItem) return
    const url = editItem.image_url || imageUrlInput.trim()
    if (!url) return
    setFetchingToR2(true)
    try {
      await api.post(`/catalog/${editItem.slug}/fetch-image`, { url })
      loadItems()
    } catch { /* error */ }
    finally { setFetchingToR2(false) }
  }

  async function handleAutoFetchImage() {
    if (!editItem) return
    const brand = getItemBrand(editItem)
    const mpn = getItemMpn(editItem)
    const url = getManufacturerImageUrl(brand, mpn)
    if (!url) return
    setImageUrlInput(url)
    setSavingImageUrl(true)
    try {
      await api.patch(`/catalog/${editItem.slug}`, { image_url: url } as any)
      loadItems()
    } catch { /* error */ }
    finally { setSavingImageUrl(false) }
  }

  async function toggleFavorite(item: CatalogSearchResult, e: React.MouseEvent) {
    e.stopPropagation()
    try {
      const res = await api.post<{ is_favorite: number }>(`/catalog/${item.slug}/favorite`)
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_favorite: res.is_favorite } : i))
    } catch { /* error */ }
  }

  async function addGlobalToMyCatalog(gp: GlobalProduct) {
    setAddingId(gp.id)
    try {
      await api.post('/catalog/add-global', {
        globalProductId: gp.id,
        cost: addForm.cost,
        sellPrice: addForm.sellPrice,
        msrp: 0,
        preferredSupplier: addForm.supplier || null,
      })
      setAddingId(null)
      setAddForm({ cost: 0, sellPrice: 0, supplier: '' })
      // Refresh both views
      if (search) loadGlobalItems()
    } catch { /* error */ }
  }

  const editBrand = editItem ? getItemBrand(editItem) : ''
  const editMpn = editItem ? getItemMpn(editItem) : ''
  const canAutoFetch = editItem && !(editItem.custom_image_r2_key || editItem.image_r2_key) && !editItem.image_url && getManufacturerImageUrl(editBrand, editMpn)
  const canSaveToR2 = editItem && !(editItem.custom_image_r2_key || editItem.image_r2_key) && (editItem.image_url || imageUrlInput.trim())

  return (
    <div className="page-enter">
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Product Catalog</h1>
          {!loading && tab === 'my-catalog' && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{total} items</span>}
        </div>
        <Button onClick={openAdd} icon={PlusIcon}>
          <span className="hidden sm:inline">Add Item</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-2" style={{ borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={() => { setTab('my-catalog'); setPage(1) }}
          style={{
            padding: '8px 16px', fontSize: 13, fontWeight: tab === 'my-catalog' ? 600 : 400,
            border: 'none', background: 'none',
            color: tab === 'my-catalog' ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: tab === 'my-catalog' ? '2px solid var(--primary)' : '2px solid transparent',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Your Catalog
        </button>
        <button
          onClick={() => { setTab('library'); setPage(1) }}
          style={{
            padding: '8px 16px', fontSize: 13, fontWeight: tab === 'library' ? 600 : 400,
            border: 'none', background: 'none',
            color: tab === 'library' ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: tab === 'library' ? '2px solid var(--primary)' : '2px solid transparent',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Global Catalog
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
        {tab === 'my-catalog'
          ? 'Products and pricing specific to your company. You can override global product pricing here.'
          : 'Shared products available to all tenants. Add items to your catalog to customize pricing.'}
      </p>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search" placeholder={tab === 'my-catalog' ? 'Search catalog...' : 'Search product library...'} value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="v3-input max-w-xs"
        />
        <div className="v3-select-wrap max-w-[160px]">
          <select
            value={laborOnly ? 'labor' : category}
            onChange={(e) => { setCategory(e.target.value); setLaborOnly(false); setPage(1) }}
            className="v3-select"
          >
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <span className="v3-select-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6,9 12,15 18,9" />
            </svg>
          </span>
        </div>
        {tab === 'my-catalog' && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFavoritesOnly(!favoritesOnly); setPage(1) }}
              style={{
                color: favoritesOnly ? 'var(--accent)' : 'var(--text-tertiary)',
                border: favoritesOnly ? '1px solid var(--accent)' : '1px solid var(--border)',
              }}
            >
              {favoritesOnly ? '★ Favorites' : '☆ Favorites'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setLaborOnly(!laborOnly); if (!laborOnly) setCategory(''); setPage(1) }}
              style={{
                color: laborOnly ? 'var(--primary)' : 'var(--text-tertiary)',
                border: laborOnly ? '1px solid var(--primary)' : '1px solid var(--border)',
              }}
            >
              Labor Items
            </Button>
          </>
        )}
      </div>

      {/* Brand pills (My Catalog only) */}
      {tab === 'my-catalog' && brands.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {brandFilter && (
            <button
              onClick={() => { setBrandFilter(''); setPage(1) }}
              style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 12,
                border: '1px solid var(--danger)', background: 'transparent',
                color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ✕ Clear
            </button>
          )}
          {brands.map(b => (
            <button
              key={b}
              onClick={() => { setBrandFilter(brandFilter === b ? '' : b); setPage(1) }}
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12,
                border: brandFilter === b ? '1px solid var(--primary)' : '1px solid var(--border)',
                background: brandFilter === b ? 'var(--primary-soft, rgba(59,130,246,0.1))' : 'transparent',
                color: brandFilter === b ? 'var(--primary)' : 'var(--text-secondary)',
                cursor: 'pointer', fontWeight: brandFilter === b ? 600 : 400,
                fontFamily: 'inherit',
              }}
            >
              {b}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {tab === 'my-catalog' ? (
        <>
          {loading ? (
            <TableSkeleton rows={8} cols={7} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No catalog items"
              description="Add products and equipment to your catalog."
              action={<Button onClick={openAdd}>Add Item</Button>}
            />
          ) : (
            <>
              <MyCatalogTable items={items} onEdit={openEdit} onFav={toggleFavorite} />
              <MyCatalogMobile items={items} onEdit={openEdit} onFav={toggleFavorite} />
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
                  <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* Product Library tab */
        <>
          {loading ? (
            <CardGridSkeleton cards={6} />
          ) : !search ? (
            <EmptyState
              title="Search the Product Library"
              description="Type a product name, brand, or MPN to browse shared products from the library."
            />
          ) : globalItems.length === 0 ? (
            <EmptyState
              title="No products found"
              description="No matching products in the library. Try a different search."
            />
          ) : (
            <div style={{ border: '1px solid var(--border-light)', borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-xs)' }}>
              {globalItems.map((gp) => {
                const isExpanded = addingId === gp.id
                return (
                  <div key={gp.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px' }}>
                      <ProductImage item={{ image_r2_key: gp.image_r2_key, image_url: gp.image_url, manufacturer: gp.brand, mpn: gp.mpn } as any} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                          <strong>{gp.brand}</strong> <span className="mono" style={{ color: 'var(--text-tertiary)' }}>{gp.mpn}</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{gp.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'flex', gap: 8 }}>
                          {gp.category && <span style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--bg-inset)', textTransform: 'capitalize' }}>{gp.category}</span>}
                          {gp.is_verified === 1 && <span style={{ color: 'var(--success)', fontWeight: 600 }}>VERIFIED</span>}
                          <span>{gp.usage_count} integrator{gp.usage_count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => { setAddingId(isExpanded ? null : gp.id); setAddForm({ cost: 0, sellPrice: 0, supplier: '' }) }}
                        style={{
                          padding: '6px 14px', fontSize: 12, fontWeight: 500,
                          border: '1px solid var(--primary)', borderRadius: 'var(--radius, 8px)',
                          background: isExpanded ? 'var(--primary)' : 'transparent',
                          color: isExpanded ? '#fff' : 'var(--primary)',
                          cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                        }}
                      >
                        {isExpanded ? 'Cancel' : 'Add to My Catalog'}
                      </button>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: '8px 16px 12px', background: 'var(--bg-inset)', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Cost ($)</label>
                          <input type="number" step="0.01" value={addForm.cost} onChange={(e) => setAddForm({ ...addForm, cost: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius, 8px)', width: 100, background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }}
                            autoFocus />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Sell Price ($)</label>
                          <input type="number" step="0.01" value={addForm.sellPrice} onChange={(e) => setAddForm({ ...addForm, sellPrice: parseFloat(e.target.value) || 0 })}
                            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius, 8px)', width: 100, background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Supplier</label>
                          <input type="text" value={addForm.supplier} onChange={(e) => setAddForm({ ...addForm, supplier: e.target.value })}
                            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius, 8px)', width: 140, background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }}
                            placeholder="Optional" />
                        </div>
                        <Button onClick={() => addGlobalToMyCatalog(gp)} size="sm">
                          Add to My Catalog
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); setEditItem(null) }} title={editItem ? 'Edit Product' : 'Add Catalog Item'}>
        {/* Image section */}
        {editItem && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Product Image</label>
            <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
              <div style={{ width: 120, height: 120, borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-inset)', overflow: 'hidden', flexShrink: 0 }}>
                <ProductImage item={{ image_r2_key: editItem.custom_image_r2_key || editItem.image_r2_key || null, image_url: editItem.image_url || null, manufacturer: editBrand, mpn: editMpn } as any} size={120} />
              </div>
              <div className="space-y-1">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0]) }} />
                <Button variant="ghost" size="sm" type="button" onClick={() => fileRef.current?.click()} style={{ display: 'block' }} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload File'}
                </Button>
                {canAutoFetch && (
                  <Button variant="ghost" size="sm" type="button" onClick={handleAutoFetchImage} style={{ display: 'block', color: 'var(--primary)' }}>
                    Auto-fetch Image
                  </Button>
                )}
                {canSaveToR2 && (
                  <Button variant="ghost" size="sm" type="button" onClick={handleFetchToR2} style={{ display: 'block', color: 'var(--success)' }} disabled={fetchingToR2}>
                    {fetchingToR2 ? 'Saving...' : 'Save to local storage (R2)'}
                  </Button>
                )}
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                  {(editItem.custom_image_r2_key || editItem.image_r2_key) ? 'Source: R2 storage' : editItem.image_url ? 'Source: URL' : 'No image'}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="Paste image URL..."
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                className="v3-input"
                style={{ fontSize: 12, flex: 1 }}
              />
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={handleSaveImageUrl}
                disabled={savingImageUrl || !imageUrlInput.trim()}
                style={{ flexShrink: 0 }}
              >
                {savingImageUrl ? '...' : 'Set URL'}
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Name *</label>
            <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="v3-input" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>MPN</label>
              <input value={formData.mpn} onChange={(e) => setFormData({ ...formData, mpn: e.target.value })} className="v3-input" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Manufacturer</label>
              <input value={formData.manufacturer} onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} className="v3-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Category</label>
              <div className="v3-select-wrap">
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="v3-select">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label || 'Select...'}</option>)}
                </select>
                <span className="v3-select-chevron">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9" />
                  </svg>
                </span>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Supplier</label>
              <input value={formData.preferred_supplier} onChange={(e) => setFormData({ ...formData, preferred_supplier: e.target.value })} className="v3-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Cost ($)</label>
              <input type="number" step="0.01" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })} className="v3-input" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Sell Price ($)</label>
              <input type="number" step="0.01" value={formData.sell_price} onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || 0 })} className="v3-input" />
            </div>
          </div>
          {formData.sell_price > 0 && (
            <div style={{ fontSize: 12, color: formData.sell_price > formData.cost ? 'var(--success)' : 'var(--danger)', fontFamily: 'var(--font-mono)' }}>
              Margin: {((formData.sell_price - formData.cost) / formData.sell_price * 100).toFixed(1)}%
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="v3-textarea" rows={3} />
          </div>
          <Button type="submit" loading={saving} className="w-full">
            {editItem ? 'Update Product' : 'Add to Catalog'}
          </Button>
        </form>

        {/* Physical Specs section — only for editing existing products */}
        {editItem && (
          <div style={{
            marginTop: 20, paddingTop: 16,
            borderTop: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
              Physical Specs
            </div>
            {specsLoading ? (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading specs...</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Form Factor</label>
                    <div className="v3-select-wrap">
                      <select
                        value={specsForm.form_factor}
                        onChange={(e) => setSpecsForm({ ...specsForm, form_factor: e.target.value })}
                        className="v3-select"
                      >
                        <option value="">Select...</option>
                        <option value="rack_mount">Rack Mount</option>
                        <option value="wall_mount">Wall Mount</option>
                        <option value="desktop">Desktop</option>
                        <option value="ceiling">Ceiling</option>
                        <option value="other">Other</option>
                      </select>
                      <span className="v3-select-chevron">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6,9 12,15 18,9" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Rack Units (U)</label>
                    <input
                      type="number" min="0" step="1"
                      value={specsForm.rack_units || ''}
                      onChange={(e) => setSpecsForm({ ...specsForm, rack_units: parseInt(e.target.value) || 0 })}
                      className="v3-input"
                      placeholder="1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Weight (lbs)</label>
                    <input
                      type="number" min="0" step="0.1"
                      value={specsForm.weight_lbs || ''}
                      onChange={(e) => setSpecsForm({ ...specsForm, weight_lbs: parseFloat(e.target.value) || 0 })}
                      className="v3-input"
                      placeholder="7.5"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Power Draw (watts)</label>
                    <input
                      type="number" min="0" step="1"
                      value={specsForm.power_draw_watts || ''}
                      onChange={(e) => setSpecsForm({ ...specsForm, power_draw_watts: parseFloat(e.target.value) || 0 })}
                      className="v3-input"
                      placeholder="12"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleSaveSpecs}
                  loading={savingSpecs}
                  className="w-full"
                >
                  Save Physical Specs
                </Button>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  Physical specs are shared across all tenants using this product.
                </p>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

function FavStar({ item, onClick }: { item: CatalogSearchResult; onClick: (item: CatalogSearchResult, e: React.MouseEvent) => void }) {
  const isFav = item.is_favorite === 1
  return (
    <button onClick={(e) => onClick(item, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
      <svg width={16} height={16} viewBox="0 0 24 24" fill={isFav ? 'var(--accent)' : 'none'} stroke={isFav ? 'var(--accent)' : 'var(--text-tertiary)'} strokeWidth={2}>
        <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
      </svg>
    </button>
  )
}

interface TableProps {
  items: CatalogSearchResult[]
  onEdit: (item: CatalogSearchResult) => void
  onFav: (item: CatalogSearchResult, e: React.MouseEvent) => void
}

function MyCatalogTable({ items, onEdit, onFav }: TableProps) {
  return (
    <div className="hidden sm:block" style={{ border: '1px solid var(--border-light)', borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-xs)' }}>
      <table className="v3-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--bg-inset)', borderBottom: '1px solid var(--border-light)' }}>
            <th style={{ ...TH, width: 28 }} />
            <th style={{ ...TH, width: 44 }} />
            <th style={TH}>Name</th>
            <th style={TH}>MPN</th>
            <th style={TH}>Brand</th>
            <th style={TH}>Category</th>
            <th style={{ ...TH, textAlign: 'right' }}>Cost</th>
            <th style={{ ...TH, textAlign: 'right' }}>Sell</th>
            <th style={{ ...TH, textAlign: 'right', width: 60 }}>Margin</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const margin = item.sell_price > 0 ? ((item.sell_price - item.cost) / item.sell_price * 100) : 0
            const name = getItemName(item)
            const brand = getItemBrand(item)
            const mpn = getItemMpn(item)
            return (
              <tr key={item.id} onClick={() => onEdit(item)} style={{ borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}>
                <td style={{ padding: '6px 8px' }}><FavStar item={item} onClick={onFav} /></td>
                <td style={{ padding: '6px 8px' }}><ProductImage item={{ image_r2_key: item.custom_image_r2_key || item.image_r2_key || null, image_url: item.image_url || null, manufacturer: brand, mpn } as any} size={36} /></td>
                <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {name}
                    {item.global_product_id && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--accent-m)', color: 'var(--accent)', fontWeight: 600 }}>OVERRIDE</span>}
                  </div>
                  {item.preferred_supplier && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{item.preferred_supplier}</div>}
                </td>
                <td className="mono" style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>{mpn || '-'}</td>
                <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>{brand || '-'}</td>
                <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {(item.category || item.custom_category) && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'var(--bg-inset)', fontSize: 11, textTransform: 'capitalize' }}>{item.category || item.custom_category}</span>}
                </td>
                <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, color: 'var(--text-tertiary)' }}>{formatCurrency(item.cost)}</td>
                <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{formatCurrency(item.sell_price)}</td>
                <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: margin > 0 ? 'var(--success)' : 'var(--text-tertiary)' }}>
                  {margin > 0 ? `${margin.toFixed(0)}%` : '-'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function MyCatalogMobile({ items, onEdit, onFav }: TableProps) {
  return (
    <div className="sm:hidden space-y-3">
      {items.map((item) => {
        const name = getItemName(item)
        const brand = getItemBrand(item)
        const mpn = getItemMpn(item)
        return (
          <GlowCard key={item.id} onClick={() => onEdit(item)} style={{ padding: 12 }} delay={0}>
            <div className="flex items-center gap-3">
              <ProductImage item={{ image_r2_key: item.custom_image_r2_key || item.image_r2_key || null, image_url: item.image_url || null, manufacturer: brand, mpn } as any} size={36} />
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{name}</p>
                <div className="flex justify-between items-center mt-1">
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{brand} {mpn ? <span className="mono" style={{ color: 'var(--text-tertiary)' }}>· {mpn}</span> : ''}</span>
                  <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(item.sell_price)}</span>
                </div>
              </div>
              <FavStar item={item} onClick={onFav} />
            </div>
          </GlowCard>
        )
      })}
    </div>
  )
}
